import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import {
  useOutageNotificationStore,
  RETENTION_MS,
  type OutageNotificationEntry,
} from "../../stores/useOutageNotificationStore";
import { getCauseDisplay } from "../telemetry-outage/utils";

/**
 * Admin notification panel (JAA-1096) — outage-only for now, per design
 * review: no dispatch/battery/maintenance/settlement/onboarding types exist
 * on the backend's notification surface, and we can't add a new API just
 * for this. Deliberate departures from the original Figma mock:
 *
 * - No date-based "Today"/"Earlier" grouping — a flat, reverse-chronological
 *   list instead (the store already prepends new entries, so no re-sort is
 *   needed here), bounded by RETENTION_MS (useOutageNotificationStore —
 *   pruned there on every write; re-filtered here too as a safety net for a
 *   tab that's stayed open long enough with no new event to trigger that).
 * - No "Critical" filter — tried cross-checking an entry's facility against
 *   useFleetOutageStore's live state, but that's a per-facility check, not
 *   per-incident: with no backend field to say which stored "opened" entry
 *   the CURRENT ongoing outage corresponds to, any past "opened" entry for
 *   that facility (including ones whose "resolved" entry never landed, e.g.
 *   a dropped event) re-lit as critical the moment that facility had any
 *   new ongoing outage. Removed rather than built out further.
 */

type FilterKey = "all" | "unread";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
];

const EMPTY_MESSAGE: Record<FilterKey, string> = {
  all: "No notifications yet",
  unread: "No unread notifications",
};

function formatRelativeTime(timestampMs: number): string {
  const mins = Math.floor((Date.now() - timestampMs) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function matchesFilter(entry: OutageNotificationEntry, filter: FilterKey): boolean {
  if (filter === "unread") return !entry.read;
  return true;
}

function NotificationItem({
  entry,
  onViewOutage,
}: {
  entry: OutageNotificationEntry;
  onViewOutage: (entry: OutageNotificationEntry) => void;
}) {
  const cause = getCauseDisplay(entry.causeAtDetection);
  const Icon = cause.icon;
  const facilityLabel = entry.facilityName ?? "A facility";
  const title = entry.isOngoing
    ? `${facilityLabel} outage detected`
    : `${facilityLabel} outage resolved`;
  const accentColor = entry.isOngoing ? "#D92D20" : "#17B26A";

  return (
    <div
      data-testid="notification-item"
      className="flex gap-3 border-b border-[#F5F5F5] px-4 py-3 last:border-b-0"
      style={{ borderLeft: `2px solid ${accentColor}` }}
    >
      <Icon className="mt-0.5 size-4 shrink-0" style={{ color: accentColor }} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[13px] font-semibold text-[#181D27]">{title}</p>
          <span
            className="mt-1 size-2 shrink-0 rounded-full"
            style={{ backgroundColor: accentColor }}
          />
        </div>
        <p className="text-[12px] text-[#717680]">{cause.label}</p>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] text-[#717680]">
            <span className="rounded bg-[#F5F5F5] px-1.5 py-0.5 font-medium">
              Outage
            </span>
            <span>{facilityLabel}</span>
            <span>·</span>
            <span>{formatRelativeTime(entry.timestamp)}</span>
          </div>
          <button
            type="button"
            onClick={() => onViewOutage(entry)}
            className="shrink-0 cursor-pointer text-[12px] font-semibold"
            style={{ color: accentColor }}
          >
            View outage →
          </button>
        </div>
      </div>
    </div>
  );
}

export interface NotificationPanelProps {
  /** Called right after navigating via "View outage" — the caller (Header)
   *  uses this to close the popover, since staying open over the page the
   *  admin just navigated to would be confusing. */
  onNavigate?: () => void;
}

export default function NotificationPanel({ onNavigate }: NotificationPanelProps) {
  const allEntries = useOutageNotificationStore((s) => s.entries);
  const markRead = useOutageNotificationStore((s) => s.markRead);
  const markAllRead = useOutageNotificationStore((s) => s.markAllRead);
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>("all");

  // 7-day retention (JAA-1096) — pruned at display time, not in the store,
  // so it self-heals regardless of how long it's been since the last event.
  // Computed once per mount — a popover doesn't stay open long enough for a
  // fixed cutoff to drift meaningfully. Date.now() inside useMemo(..., [])
  // is intentional: this is a display-filter threshold, not state React
  // needs to reconcile, so no visible inconsistency results.
  const retentionCutoff = useMemo(
    // eslint-disable-next-line react-hooks/purity
    () => Date.now() - RETENTION_MS,
    [],
  );
  const entries = allEntries.filter((entry) => entry.timestamp >= retentionCutoff);

  const unreadCount = entries.filter((e) => !e.read).length;
  const counts: Record<FilterKey, number> = {
    all: entries.length,
    unread: unreadCount,
  };

  // Already newest-first — the store prepends new entries on arrival.
  const visibleEntries = entries.filter((entry) => matchesFilter(entry, filter));

  const handleViewOutage = (entry: OutageNotificationEntry) => {
    markRead(entry.id);
    navigate(`/organizations/facility/${entry.facilityId}/telemetry-outage`);
    onNavigate?.();
  };

  return (
    <div className="w-[360px]" data-testid="notification-panel">
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-[15px] font-semibold text-[#181D27]">Notifications</h3>
          {unreadCount > 0 && (
            <span className="rounded-full bg-[#D92D20] px-[6px] py-[2px] text-[10px] font-bold text-white">
              {unreadCount} new
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => markAllRead()}
          disabled={unreadCount === 0}
          className="flex items-center gap-1 text-[12px] font-semibold text-[#1876F2] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Check className="size-3.5" />
          Mark all read
        </button>
      </div>

      <div className="flex gap-1 px-4 pb-3">
        {FILTERS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={`cursor-pointer rounded-full px-[10px] py-1 text-[12px] font-semibold transition-colors ${
              filter === key ? "bg-[#1876F2] text-white" : "bg-[#FAFAFA] text-[#717680]"
            }`}
          >
            {label} {counts[key]}
          </button>
        ))}
      </div>

      <div className="border-t border-[#D5D7DA]" />

      <div className="max-h-[400px] overflow-y-auto">
        {visibleEntries.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-[#717680]">
            {EMPTY_MESSAGE[filter]}
          </p>
        ) : (
          visibleEntries.map((entry) => (
            <NotificationItem key={entry.id} entry={entry} onViewOutage={handleViewOutage} />
          ))
        )}
      </div>
    </div>
  );
}
